import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { CtaBand } from '../components/CtaBand';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION } from '../config/foundation';

export function GetInvolved() {
  usePageTitle('Get Involved');
  const mailto = (subject: string) => `mailto:${FOUNDATION.email}?subject=${encodeURIComponent(subject)}`;

  const ways = [
    {
      title: 'Support our work',
      desc: "Donations help fund places on our programmes, the tools that power them, and grants for those who need them most. We'll be launching online giving soon — for now, get in touch and we'll arrange the best way for you to give.",
      href: mailto('I would like to support The LifeSpark Foundation'),
      cta: 'Get in touch to give',
    },
    {
      title: 'Volunteer &amp; mentor',
      desc: "Share your experience by coaching, mentoring, or supporting a participant's journey. Mentors are at the heart of how our impact spreads from one person to the next.",
      href: mailto('Volunteering with The LifeSpark Foundation'),
      cta: 'Volunteer with us',
    },
    {
      title: 'Partner with us',
      desc: 'Charities, employers, and community groups can collaborate with us to reach more people, refer participants, or co-deliver programmes.',
      href: mailto('Partnering with The LifeSpark Foundation'),
      cta: 'Explore partnership',
    },
    {
      title: 'Fundraise',
      desc: "Take on a challenge, hold an event, or rally your community. We'll support you with everything you need to fundraise for the Foundation.",
      href: mailto('Fundraising for The LifeSpark Foundation'),
      cta: 'Start fundraising',
    },
  ];

  return (
    <Layout>
      <PageHero
        eyebrow="Get Involved"
        title={<>Help <span className="text-mustard-gold">spark change</span></>}
      >
        Every contribution &mdash; time, skills, or funding &mdash; helps someone take the next
        step toward independence, and the confidence to lift others.
      </PageHero>

      {/* Ways to help */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ways.map((w, i) => (
              <Reveal key={w.cta} delay={i * 120}>
                <WayCard {...w} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="bg-white py-16 sm:py-24 border-y border-slate-100">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p className="eyebrow mb-4">Your Impact</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-display mb-6">
              <span className="text-gradient">A gift that keeps giving</span>
            </h2>
            <p className="text-slate-500 leading-relaxed mb-4">
              When you support The LifeSpark Foundation, you don't just help one person &mdash; you
              help create a mentor. Our programmes are designed so that those we empower go on to
              support and mentor others, multiplying the impact of every contribution.
            </p>
            <p className="text-slate-400 text-sm">
              {FOUNDATION.name} is a {FOUNDATION.structure} registered in England and Wales
              (Charity No. {FOUNDATION.charityNumber}).
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="max-w-2xl mx-auto">
            <div className="card p-8 sm:p-10 text-center">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-mustard-green-dark mb-3">
                Talk to us
              </h2>
              <p className="text-slate-500 leading-relaxed mb-6">
                However you'd like to get involved, we'd love to hear from you. Drop us a line and a
                member of the team will get back to you.
              </p>
              <a href={mailto('Hello from the website')} className="btn-primary inline-block">
                Email {FOUNDATION.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Looking for support yourself?"
        subtitle="If you'd like to take part in one of our programmes, find out how to apply."
      >
        <Link to="/apply" className="btn-gold">Apply for Support</Link>
      </CtaBand>
    </Layout>
  );
}

function WayCard({ title, desc, href, cta }: { title: string; desc: string; href: string; cta: string }) {
  return (
    <div className="card h-full p-6 sm:p-8 flex flex-col">
      <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-2" dangerouslySetInnerHTML={{ __html: title }} />
      <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1">{desc}</p>
      <a href={href} className="btn-secondary self-start">{cta}</a>
    </div>
  );
}
